import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Zap, ShoppingCart, UserPlus, Package, Truck, CreditCard, Users, LogIn, Clock, XCircle, RotateCcw } from 'lucide-react';

const triggerIcons = {
  checkout_abandoned: ShoppingCart,
  order_created: Package,
  order_paid: CreditCard,
  order_ready_for_dispatch: Package,
  order_fulfilled: Package,
  order_shipped: Truck,
  order_out_for_delivery: Truck,
  order_delivered: Truck,
  order_delivery_failed: XCircle,
  order_cancelled: XCircle,
  order_refunded: RotateCcw,
  payment_received: CreditCard,
  payment_failed: CreditCard,
  lead_created: UserPlus,
  customer_created: Users,
  credits_added: CreditCard,
  credits_expiring: Clock,
  credits_expired: Clock,
  awb_generated: Truck,
  courier_assigned: Truck,
  courier_picked_up: Package,
  courier_in_transit: Truck,
  courier_delivered: Truck,
  courier_failed: XCircle,
  courier_rto: RotateCcw,
  manual: Zap,
  default: Zap,
};

const triggerLabels = {
  checkout_abandoned: 'Checkout Abandoned',
  order_created: 'Order Created',
  order_paid: 'Order Paid',
  order_ready_for_dispatch: 'Ready For Dispatch',
  order_fulfilled: 'Order Fulfilled',
  order_shipped: 'Order Shipped',
  order_out_for_delivery: 'Out for Delivery',
  order_delivered: 'Order Delivered',
  order_delivery_failed: 'Order Delivery Failed',
  order_cancelled: 'Order Cancelled',
  order_refunded: 'Order Refunded',
  payment_received: 'Payment Received',
  payment_failed: 'Payment Failed',
  lead_created: 'Lead Created',
  customer_created: 'Customer Created',
  credits_added: 'Credits Added',
  credits_expiring: 'Credits Expiring',
  credits_expired: 'Credits Expired',
  awb_generated: 'AWB Generated',
  courier_assigned: 'Courier Assigned',
  courier_picked_up: 'Courier Picked Up',
  courier_in_transit: 'Courier In Transit',
  courier_delivered: 'Courier Delivered',
  courier_failed: 'Failed Delivery Attempt (NDR)',
  courier_rto: 'RTO (Return to Origin)',
  manual: 'Manual Entry',
};

function TriggerNodeComponent({ data, selected }) {
  const Icon = triggerIcons[data.trigger_type] || triggerIcons.default;
  const label = triggerLabels[data.trigger_type] || data.label || 'Trigger';
  const showAnalytics = data.showAnalytics;
  const analytics = data.analytics;

  return (
    <div
      className={`
        relative px-4 py-3 rounded-xl border-2 min-w-[180px]
        bg-gradient-to-br from-green-500/20 to-green-600/10
        ${selected ? 'border-green-400 shadow-lg shadow-green-500/30' : 'border-green-500/50'}
        transition-all duration-200
      `}
    >
      {/* Node Header */}
      <div className="flex items-center gap-2 mb-1">
        <div className="p-1.5 rounded-lg bg-green-500/30">
          <Icon className="h-4 w-4 text-green-400" />
        </div>
        <span className="text-xs font-medium text-green-400 uppercase tracking-wide">
          Trigger
        </span>
      </div>
      
      {/* Node Label */}
      <div className="text-sm font-semibold text-white">
        {label}
      </div>
      
      {/* Inline Analytics - Entry count */}
      {showAnalytics && analytics && (
        <div className="mt-2 pt-2 border-t border-green-500/30 flex items-center justify-center gap-1">
          <LogIn className="h-3 w-3 text-green-400" />
          <span className="text-xs font-bold text-white">{analytics.sent || analytics.entered || 0}</span>
          <span className="text-[10px] text-wt-text-muted">entries</span>
        </div>
      )}
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-green-400 !border-2 !border-green-600"
      />
    </div>
  );
}

export const TriggerNode = memo(TriggerNodeComponent);
